import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ListContextValue, ListProviderProps } from "./types";

const ListContext = createContext<ListContextValue | null>(null);

/** Compare two sets by content (order-independent) */
function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) {
    if (!b.has(id)) return false;
  }
  return true;
}

export function ListProvider({
  children,
  onRowClick,
  onSelectionChange,
  selectedIds: externalSelectedIds,
}: ListProviderProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => externalSelectedIds ?? new Set<string>(),
  );

  // Track whether we are syncing from external to avoid notifying back
  const isSyncingRef = useRef(false);

  // Sync FROM external → internal (only when the external prop changes)
  useEffect(() => {
    if (externalSelectedIds) {
      setSelectedIds((prev) => {
        if (setsEqual(externalSelectedIds, prev)) return prev;
        isSyncingRef.current = true;
        return externalSelectedIds;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSelectedIds]);

  // Sync FROM internal → external (notify parent of changes)
  useEffect(() => {
    if (isSyncingRef.current) {
      isSyncingRef.current = false;
      return;
    }
    onSelectionChange?.(selectedIds);
  }, [selectedIds, onSelectionChange]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set<string>());
  }, []);

  const addToSelection = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
  }, []);

  const removeFromSelection = useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);

  const value: ListContextValue = {
    state: { selectedIds },
    actions: {
      toggle,
      selectAll,
      deselectAll,
      addToSelection,
      removeFromSelection,
    },
    meta: { onRowClick },
  };

  return <ListContext value={value}>{children}</ListContext>;
}

export function useList(): ListContextValue {
  const context = use(ListContext);
  if (!context) {
    throw new Error("useList must be used within a List.Provider");
  }
  return context;
}
